"use client";

import {
  ChevronDownIcon,
  ChevronRightIcon,
  SendIcon,
  TerminalIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { PendingToolCall } from "../types";
import { cn, formatArguments } from "../utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ToolResponseMessage {
  role: "tool";
  content: [{ type: "text"; text: string }];
  tool_call_id: string;
  name: string;
}

export interface ToolResponsePanelProps {
  pendingToolCalls: PendingToolCall[];
  onSubmit: (messages: ToolResponseMessage[]) => void;
  isLoading?: boolean;
  /** Additional className for the outermost wrapper. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ToolResponsePanel = ({
  pendingToolCalls,
  onSubmit,
  isLoading,
  className,
}: ToolResponsePanelProps) => {
  const [responses, setResponses] = useState<Record<string, string>>(() =>
    Object.fromEntries(pendingToolCalls.map((tc) => [tc.tool_call_id, ""])),
  );
  const [expandedArgs, setExpandedArgs] = useState<Set<string>>(new Set());
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const firstCallId = pendingToolCalls[0]?.tool_call_id;

  // Auto-focus the first textarea on mount
  useEffect(() => {
    if (firstCallId) {
      // Defer to let the DOM settle after render
      requestAnimationFrame(() => {
        textareaRefs.current[firstCallId]?.focus();
      });
    }
  }, [firstCallId]);

  const handleResponseChange = useCallback(
    (toolCallId: string, value: string) => {
      setResponses((prev) => ({ ...prev, [toolCallId]: value }));
    },
    [],
  );

  const toggleArgs = useCallback((toolCallId: string) => {
    setExpandedArgs((prev) => {
      const next = new Set(prev);
      if (next.has(toolCallId)) {next.delete(toolCallId);}
      else {next.add(toolCallId);}
      return next;
    });
  }, []);

  const allResponsesFilled = pendingToolCalls.every(
    (tc) => (responses[tc.tool_call_id] ?? "").trim().length > 0,
  );

  const handleSubmit = useCallback(() => {
    if (!allResponsesFilled || isLoading) {return;}

    const messages: ToolResponseMessage[] = pendingToolCalls.map((tc) => ({
      role: "tool" as const,
      content: [
        { type: "text" as const, text: responses[tc.tool_call_id] ?? "" },
      ],
      tool_call_id: tc.tool_call_id,
      name: tc.name,
    }));

    onSubmit(messages);
    setResponses(
      Object.fromEntries(pendingToolCalls.map((tc) => [tc.tool_call_id, ""])),
    );
  }, [allResponsesFilled, isLoading, pendingToolCalls, responses, onSubmit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === "Enter" &&
        allResponsesFilled &&
        !isLoading
      ) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [allResponsesFilled, isLoading, handleSubmit],
  );

  const isSingleCall = pendingToolCalls.length === 1;

  return (
    <div
      className={cn(
        "flex flex-col border-t border-amber-200 bg-amber-50/50",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-1.5 px-3 pb-1 pt-2.5">
        <TerminalIcon className="h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
        <span className="text-xs font-medium text-amber-700">
          {isSingleCall
            ? `Respond to tool call: ${pendingToolCalls[0].name}`
            : `Respond to ${pendingToolCalls.length} tool calls`}
        </span>
      </div>

      {/* Tool call response forms */}
      <div
        className={cn(
          "flex flex-col gap-2 px-3 pb-2",
          !isSingleCall && "max-h-[50vh] overflow-y-auto",
        )}
      >
        {pendingToolCalls.map((tc) => {
          const isExpanded = expandedArgs.has(tc.tool_call_id);
          const formattedArgs = formatArguments(tc.arguments);
          const hasArgs = tc.arguments !== "{}" && tc.arguments !== "";

          return (
            <div
              key={tc.tool_call_id}
              className={cn(
                "flex flex-col gap-1.5 rounded-md border bg-white p-2",
                isSingleCall ? "border-amber-200" : "border-amber-200/80",
              )}
            >
              {/* Tool call info */}
              <div className="flex items-center gap-1.5">
                {!isSingleCall && (
                  <span className="text-[11px] font-medium text-amber-700">
                    {tc.name}
                  </span>
                )}
                {hasArgs && (
                  <button
                    type="button"
                    onClick={() => toggleArgs(tc.tool_call_id)}
                    className={cn(
                      "flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-amber-600 transition-colors hover:bg-amber-100",
                      isSingleCall && "ml-0",
                      !isSingleCall && "ml-auto",
                    )}
                  >
                    {isExpanded ? (
                      <ChevronDownIcon className="h-3 w-3" />
                    ) : (
                      <ChevronRightIcon className="h-3 w-3" />
                    )}
                    args
                  </button>
                )}
                <span className="ml-auto text-[9px] text-gray-400">
                  {tc.tool_call_id}
                </span>
              </div>

              {/* Collapsible arguments */}
              {isExpanded && hasArgs && (
                <pre className="max-h-32 overflow-auto rounded bg-gray-50 p-2 font-mono text-[11px] leading-relaxed text-gray-600">
                  {formattedArgs}
                </pre>
              )}

              {/* Response textarea */}
              <textarea
                ref={(el) => {
                  textareaRefs.current[tc.tool_call_id] = el;
                }}
                value={responses[tc.tool_call_id] ?? ""}
                onChange={(e) =>
                  handleResponseChange(tc.tool_call_id, e.target.value)
                }
                onKeyDown={handleKeyDown}
                placeholder={`Enter result for ${tc.name}…`}
                disabled={isLoading}
                rows={2}
                className={cn(
                  "min-h-[64px] w-full resize-y rounded border border-gray-200 px-2 py-1.5",
                  "font-mono text-xs leading-relaxed text-gray-700",
                  "placeholder:text-gray-400",
                  "focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400/50",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                )}
              />
            </div>
          );
        })}
      </div>

      {/* Submit button */}
      <div className="flex items-center justify-between border-t border-amber-200/60 px-3 py-2">
        <span className="text-[10px] text-gray-400">⌘ Enter to submit</span>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!allResponsesFilled || isLoading}
          className={cn(
            "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
            allResponsesFilled && !isLoading
              ? "bg-amber-500 text-white hover:bg-amber-600"
              : "cursor-not-allowed bg-gray-200 text-gray-400",
          )}
        >
          <SendIcon className="h-3 w-3" />
          {isSingleCall
            ? "Submit Response"
            : `Submit ${pendingToolCalls.length} Responses`}
        </button>
      </div>
    </div>
  );
};
